import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import BookmarkIcon from './icons/BookmarkIcon';
import { useFollowUps } from '../contexts/FollowUpContext';
import { useWorkingStatusPreference } from '../contexts/WorkingStatusPreferenceContext';
import type { FollowUpItem } from '../services/followUps.service';
import { readableTextOn } from '../utils/followUpColors';

// The driver detail surface. Self-fetching and keyed only by unitNumber, so the
// panel can mount it directly rather than navigate anywhere — the same thing
// DriverIconLinks does. Lazy so the driver-status-monitor bundle stays out of
// every page just because the panel is mounted on all of them.
const DriverLoadsDrawerV2 = lazy(() => import('./DriverLoadsDrawerV2'));

/** Tab position, in px from the left edge. Per-browser UI state, not user data. */
export const TAB_POSITION_STORAGE_KEY = 'follow_up_tab_x';

/** Keep the whole tab grabbable — never let it sit flush against an edge. */
const EDGE_MARGIN = 8;

/**
 * Below this, a pointer gesture is a click, not a drag. Every real click
 * jitters a pixel or two, and treating that as a drag would swallow the open.
 */
const DRAG_THRESHOLD_PX = 4;

/** Tailwind w-72. Needed in JS to decide which edge the list hangs from. */
const LIST_WIDTH_PX = 288;

/**
 * Default resting place: right of centre. The EnvironmentBanner is pinned dead
 * centre at the same top edge in test and local, so starting centred would put
 * the two on top of each other on exactly the environments developers use.
 */
const DEFAULT_FRACTION = 0.72;

const clampX = (x: number, tabWidth: number) => {
  const max = Math.max(EDGE_MARGIN, window.innerWidth - tabWidth - EDGE_MARGIN);
  return Math.min(Math.max(x, EDGE_MARGIN), max);
};

const readStoredX = (): number | null => {
  try {
    const raw = localStorage.getItem(TAB_POSITION_STORAGE_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null; // private-mode / disabled storage
  }
};

/** The route or drawer each type resolves to. */
const routeFor = (item: FollowUpItem): string | null => {
  const id = encodeURIComponent(item.item_id);
  switch (item.item_type) {
    // Search results with the drawer open, NOT /pro/:pronumber. Following up on
    // an order means glancing at it and moving on, and the standalone page
    // strands the user on a record with nothing behind it — the drawer leaves a
    // result list underneath to go back to. Same URL shape as
    // orderCardHelpers.tsx and GlobalSearch's own pro results.
    //
    // Templates ride the same param: SearchResults keeps a T-prefixed
    // pronumber raw rather than parseInt-ing it, so one route covers both.
    case 'pro':
    case 'template':
      return `/searchresults?q=${id}&pro=${id}`;
    // Straight to the lane-editor drawer, which is the live quote detail
    // surface — the standalone QuoteDetailPage is retired.
    // /order-management/quotes/:quoteId exists only to redirect external links
    // to this same URL, so routing through it would cost an extra navigation
    // for nothing. `q` is the quote NUMBER (drives the visible result list),
    // `quote` is number-revision (opens the drawer). Mirrors
    // QuoteDeepLinkRedirect.
    case 'quote':
      return `/searchresults?q=${encodeURIComponent(item.item_id.split('-')[0])}&quote=${id}`;
    // Drivers have no route; the panel mounts the moves drawer instead.
    case 'driver':
      return null;
    default:
      return null;
  }
};

interface EntryProps {
  item: FollowUpItem;
  index: number;
  onOpen: (item: FollowUpItem) => void;
  onRemove: (item: FollowUpItem) => void;
  onSaveNote: (id: number, note: string) => void;
  onSaveColor: (id: number, color: string | null) => void;
}

const FollowUpEntry: React.FC<EntryProps> = ({ item, index, onOpen, onRemove, onSaveNote, onSaveColor }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.note ?? '');
  // Escape must not commit, but blur must — and Escape blurs the input on its
  // way out. This flag lets the blur handler know the edit was abandoned.
  const abandoned = useRef(false);

  const startEditing = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(item.note ?? '');
    abandoned.current = false;
    setEditing(true);
  }, [item.note]);

  const commit = useCallback(() => {
    setEditing(false);
    if (abandoned.current) return;
    if ((draft.trim() || null) !== (item.note?.trim() || null)) onSaveNote(item.id, draft);
  }, [draft, item.id, item.note, onSaveNote]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      abandoned.current = true;
      setEditing(false);
    }
  }, [commit]);

  // Free-hex backgrounds mean the user can pick something that would hide the
  // label, so the text colour is DERIVED from the background rather than fixed.
  // No colour = no inline style at all, leaving the theme's own classes to work.
  const painted = item.color
    ? { backgroundColor: item.color, color: readableTextOn(item.color) }
    : undefined;

  return (
    <Draggable draggableId={String(item.id)} index={index}>
      {(dragProvided, dragSnapshot) => (
    <li
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      data-testid={`follow-up-entry-${item.id}`}
      style={{ ...dragProvided.draggableProps.style, ...painted }}
      className={`group border-b border-slate-100 last:border-b-0 dark:border-gray-700/60 ${
        dragSnapshot.isDragging ? 'rounded shadow-lg ring-1 ring-blue-400' : ''
      }`}
    >
      <div className="flex items-start gap-1 px-2 py-1.5 hover:bg-scrim/5 dark:hover:bg-white/5">
        {/* Dedicated grip rather than dragging the whole row. Spreading
            dragHandleProps onto the <li> gives it role="button", which nests the
            open/note/remove buttons inside a button, and makes every click on
            the row a potential drag. A grip keeps "click to open" and "drag to
            reorder" as separate gestures. */}
        <span
          {...dragProvided.dragHandleProps}
          data-testid={`follow-up-grip-${item.id}`}
          aria-label={`Reorder ${item.label}`}
          className="shrink-0 cursor-grab pt-0.5 text-slate-300 opacity-0 transition group-hover:opacity-100 active:cursor-grabbing dark:text-gray-500"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
          </svg>
        </span>
        <button
          type="button"
          onClick={() => onOpen(item)}
          aria-label={`Open ${item.label}`}
          className="min-w-0 flex-1 text-left"
          title={item.label}
        >
          <span
            className={`block truncate text-[0.8125rem] font-medium ${
              painted ? '' : 'text-slate-800 dark:text-gray-100'
            }`}
          >
            {item.label}
          </span>
        </button>

        {/* Clear, only when there is something to clear. The OS colour dialog
            has no "none", so this cannot live inside the picker itself.
            Deliberately not a second x -- the row's x already means "remove
            this bookmark", and two would be a coin toss. */}
        {item.color && (
          <button
            type="button"
            data-control="clear-colour"
            onClick={(e) => { e.stopPropagation(); onSaveColor(item.id, null); }}
            aria-label={`Clear colour on ${item.label}`}
            title="Clear colour"
            className="shrink-0 rounded p-1 opacity-0 transition focus:opacity-100 group-hover:opacity-100 hover:opacity-100"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="M5.6 5.6l12.8 12.8" />
            </svg>
          </button>
        )}

        {/* Palette: a <label> wrapping a visually hidden <input type="color">.
            Clicking the label forwards to the input, so this opens the native
            OS colour dialog directly -- no popover in between. Hover-only, and
            deliberately identical whether or not a colour is set, so a coloured
            row looks no busier at rest than a plain one.

            `onInput`, not onChange: the native dialog streams while the user
            drags through the spectrum, and onChange alone fires only on dismiss
            in some browsers, which reads as the picker doing nothing. */}
        <label
          data-control="colour"
          title="Background colour"
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 cursor-pointer rounded p-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100 hover:text-blue-600 dark:hover:text-blue-400"
        >
          <input
            type="color"
            aria-label={`Background colour for ${item.label}`}
            value={item.color ?? '#FFFFFF'}
            onInput={(e) => onSaveColor(item.id, (e.target as HTMLInputElement).value)}
            className="sr-only"
          />
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M12,1A11,11,0,0,0,1,12c0,6.07,4.93,11,11,11a3,3,0,0,0,3-3,2.94,2.94,0,0,0-.77-2,1,1,0,0,1,.75-1.66h2A6.02,6.02,0,0,0,23,10.33C22.85,5.15,17.9,1,12,1ZM6.5,13A1.5,1.5,0,1,1,8,11.5,1.5,1.5,0,0,1,6.5,13Zm2-5A1.5,1.5,0,1,1,10,6.5,1.5,1.5,0,0,1,8.5,8Zm7,0A1.5,1.5,0,1,1,17,6.5,1.5,1.5,0,0,1,15.5,8Zm2.5,4a1.5,1.5,0,1,1,1.5-1.5A1.5,1.5,0,0,1,18,12Z" />
          </svg>
        </label>

        <button
          type="button"
          data-control="note"
          onClick={startEditing}
          aria-label={item.note ? `Edit note for ${item.label}` : `Add a note to ${item.label}`}
          className="shrink-0 rounded p-1 text-slate-300 opacity-0 transition hover:text-blue-600 focus:opacity-100 group-hover:opacity-100 dark:text-gray-500 dark:hover:text-blue-400"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M21.29,2.71a3.4,3.4,0,0,0-4.81,0L3.44,15.76A1,1,0,0,0,3.17,16.3L2,21.29a1,1,0,0,0,1.21,1.2l5-1.17a1,1,0,0,0,.54-.27L21.29,7.52A3.4,3.4,0,0,0,21.29,2.71ZM7.31,19.47l-3,.7.7-3L15.14,6.05l2.31,2.31Zm12.57-13.36-1,1L16.55,4.81l1-1a1.4,1.4,0,0,1,2,0A1.4,1.4,0,0,1,19.88,6.11Z" />
          </svg>
        </button>

        <button
          type="button"
          data-control="remove"
          onClick={(e) => { e.stopPropagation(); onRemove(item); }}
          aria-label={`Remove ${item.label} from Follow Ups`}
          className="shrink-0 rounded p-1 text-slate-300 opacity-0 transition hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:text-gray-500 dark:hover:text-red-400"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      {editing ? (
        <div className="px-2 pb-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            value={draft}
            maxLength={500}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            placeholder="Why are you following up?"
            className="w-full rounded border border-slate-300 bg-panel px-1.5 py-1 text-[0.75rem] text-slate-700 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:text-gray-200"
          />
        </div>
      ) : item.note ? (
        <button
          type="button"
          onClick={startEditing}
          className={`block w-full px-2 pb-1.5 text-left text-[0.75rem] leading-snug ${
            painted ? 'opacity-80' : 'text-slate-500 dark:text-gray-400'
          }`}
        >
          {item.note}
        </button>
      ) : null}
    </li>
      )}
    </Draggable>
  );
};

/**
 * The Follow Up panel: a bookmark tab pinned to the top edge of every
 * authenticated page, dragged left and right to wherever the user wants it, and
 * dropping a list of their bookmarked work items.
 *
 * Styled after EnvironmentBanner's tab (fixed to the top, rounded-b, gradient)
 * because that shape already reads as "pinned chrome, not page content" in this
 * app. Unlike that banner, this one clamps to the viewport and remembers where
 * it was dropped — it is permanent, so a tab parked off-screen would be
 * unreachable forever rather than until the next reload.
 */
const FollowUpPanel: React.FC = () => {
  const { items, unfollow, setNote, setColor, reorderItems } = useFollowUps();
  const { compactFollowUpHandle, hideFollowUpHandleWhenEmpty } = useWorkingStatusPreference();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [x, setX] = useState<number>(() => readStoredX() ?? -1);
  const [driverUnit, setDriverUnit] = useState<string | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ startX: number; startPos: number; moved: boolean } | null>(null);
  // Set on pointerup, read by the click that immediately follows it. A drag
  // ends with a click event on the same element, so without this every
  // reposition would also toggle the list.
  const suppressClick = useRef(false);

  const tabWidth = () => tabRef.current?.getBoundingClientRect().width ?? 0;

  // First paint: resolve the default (or clamp a restored value) once the tab
  // has a measurable width. Layout effect so the tab never flashes at x=0.
  useLayoutEffect(() => {
    setX((prev) => {
      const w = tabWidth();
      if (prev < 0) return clampX(window.innerWidth * DEFAULT_FRACTION - w / 2, w);
      return clampX(prev, w);
    });
  }, []);

  // A tab dropped on a 3440px monitor and reopened on a laptop would otherwise
  // sit past the right edge, ungrabbable.
  useEffect(() => {
    const onResize = () => setX((prev) => clampX(prev, tabWidth()));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    drag.current = { startX: e.clientX, startPos: x, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom / no capture */ }
  }, [x]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
    d.moved = true;
    // Horizontal only — the tab is pinned to the top edge by design.
    setX(clampX(d.startPos + dx, tabWidth()));
  }, []);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    if (!d?.moved) return;
    suppressClick.current = true;
    setX((prev) => {
      const next = clampX(prev, tabWidth());
      try { localStorage.setItem(TAB_POSITION_STORAGE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const onTabClick = useCallback(() => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    setOpen((o) => !o);
  }, []);

  const openItem = useCallback((item: FollowUpItem) => {
    setOpen(false);
    const route = routeFor(item);
    if (route) navigate(route);
    else if (item.item_type === 'driver') setDriverUnit(item.item_id);
  }, [navigate]);

  const removeItem = useCallback((item: FollowUpItem) => {
    void unfollow(item.item_type, item.item_id);
  }, [unfollow]);

  const saveNote = useCallback((id: number, note: string) => {
    void setNote(id, note);
  }, [setNote]);

  const saveColor = useCallback((id: number, color: string | null) => {
    void setColor(id, color);
  }, [setColor]);

  /**
   * Commit a drag. A drop outside the list (`destination` null) or back onto the
   * same index is a cancelled gesture, not a reorder — writing either would
   * stamp sort_order on every row for nothing, which matters because doing so
   * changes where the NEXT new bookmark lands relative to them.
   */
  const onDragEnd = useCallback((result: DropResult) => {
    const to = result.destination?.index;
    if (to == null || to === result.source.index) return;
    const ordered = items.map((i) => i.id);
    const [moved] = ordered.splice(result.source.index, 1);
    ordered.splice(to, 0, moved);
    void reorderItems(ordered);
  }, [items, reorderItems]);

  // The tab clamps itself to the viewport, but the list is far wider than the
  // tab: parked near the right edge, a left-anchored list would hang off screen
  // with its entries unclickable. Flip it to the tab's right edge instead.
  // Read at render time rather than stored — it depends on both x and the
  // current window width, and the resize listener already re-renders on both.
  const listAnchor =
    x >= 0 && x + LIST_WIDTH_PX > window.innerWidth - EDGE_MARGIN ? 'right-0' : 'left-0';

  /**
   * "Hide Follow Up Handle When Empty": the tab disappears until something is
   * bookmarked. Two deliberate exceptions, both about not yanking UI out from
   * under someone mid-gesture:
   *   - the panel is OPEN — removing the last entry would otherwise tear the
   *     whole thing away under the cursor, so it survives until closed;
   *   - a driver drawer is open — the panel MOUNTS that drawer, so hiding here
   *     would close a drawer the user is still reading.
   */
  if (hideFollowUpHandleWhenEmpty && items.length === 0 && !open && !driverUnit) return null;

  return (
    <>
      <div
        ref={wrapRef}
        className="fixed top-0 z-[9998] w-fit select-none"
        /**
         * `left`, NOT `transform: translateX(...)`. A transform here would create
         * a containing block, and @hello-pangea/dnd positions its drag clone
         * with `position: fixed` — so a dragged entry would resolve against this
         * wrapper instead of the viewport and fly out of the panel, offset by
         * exactly the tab's x. `left` moves the tab without establishing one.
         */
        style={{ left: `${Math.max(x, 0)}px` }}
      >
        <button
          ref={tabRef}
          type="button"
          data-testid="follow-up-tab"
          aria-expanded={open}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={onTabClick}
          aria-label={compactFollowUpHandle ? `Follow Ups (${items.length})` : undefined}
          className={`flex cursor-grab items-center gap-1.5 rounded-b-lg bg-gradient-to-r from-blue-500 to-blue-700 py-2 text-sm font-medium text-white shadow-lg hover:opacity-90 focus:outline-none active:cursor-grabbing ${
            compactFollowUpHandle ? 'px-2.5' : 'gap-2 px-4'
          }`}
        >
          <BookmarkIcon filled className="w-4 h-4" />
          {/* Compact drops the wording, so the accessible name above has to
              carry it -- and the count still shows at 0, or the handle reads as
              a stray piece of chrome with no clue what it opens. */}
          <span>{compactFollowUpHandle ? items.length : `Follow-Ups - (${items.length})`}</span>
        </button>

        {open && (
          <div
            role="dialog"
            aria-label="Follow Ups"
            className={`absolute top-full ${listAnchor} mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800`}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1.5 dark:border-gray-700">
              <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                Follow Ups
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Follow Ups"
                className="rounded p-0.5 text-slate-400 hover:text-slate-700 dark:text-gray-500 dark:hover:text-gray-200"
              >
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            {items.length === 0 ? (
              <p className="px-3 py-4 text-center text-[0.75rem] text-slate-500 dark:text-gray-400">
                Nothing to follow up on yet. Bookmark a pro, quote, template, or driver to see it here.
              </p>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="follow-ups">
                  {(dropProvided) => (
                    <ul
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className="max-h-80 overflow-y-auto"
                    >
                      {items.map((item, i) => (
                        <FollowUpEntry
                          key={item.id}
                          item={item}
                          index={i}
                          onOpen={openItem}
                          onRemove={removeItem}
                          onSaveNote={saveNote}
                          onSaveColor={saveColor}
                        />
                      ))}
                      {dropProvided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        )}
      </div>

      {/* Only mounted once a driver entry is opened (lazy chunk). */}
      {driverUnit && (
        <Suspense fallback={null}>
          <DriverLoadsDrawerV2
            isOpen
            onClose={() => setDriverUnit(null)}
            unitNumber={driverUnit}
          />
        </Suspense>
      )}
    </>
  );
};

export default FollowUpPanel;
